import { ZodSchema } from "zod";
import { View } from "@common/types/views/views";

interface ParseTaskViewParams<TView extends View> {
    schema: ZodSchema<TView>;
    raw: object | null | undefined;
    createDefaultView: () => TView;
}

export function parseTaskView<TView extends View>({
    schema,
    raw,
    createDefaultView,
}: ParseTaskViewParams<TView>): TView {
    const parsed = schema.safeParse(raw);
    return parsed.success ? parsed.data : createDefaultView();
}

export const withBulkActions = <TView extends View>(view: TView): TView => ({
    ...view,
    options: { ...view.options, selection: true, bulkActions: true },
});

export const withoutBulkActions = <TView extends View>(view: TView): TView => ({
    ...view,
    options: { ...view.options, selection: false, bulkActions: false },
});

interface RestrictViewConfigColumnsParams<
    TConfig extends { columns: Record<string, unknown> },
> {
    config: TConfig;
    allowedColumnIds?: ReadonlySet<string>;
}

export function restrictViewConfigColumns<
    TConfig extends { columns: Record<string, unknown> },
>({
    config,
    allowedColumnIds,
}: RestrictViewConfigColumnsParams<TConfig>): TConfig {
    if (!allowedColumnIds) {
        return config;
    }
    return {
        ...config,
        columns: Object.fromEntries(
            Object.entries(config.columns).filter(([id]) =>
                allowedColumnIds.has(id),
            ),
        ),
    };
}
